import EventBus from '@/event-bus';
import _Singleton from '@/tools/singleton';
import urlTool from '@/tools/url';
import Products from '@/products';
import Shop from '@/shop';
import stripHtml from '@/helpers/stripHtml';

import options from '@/ui/ui_ajax-products/product/default-selectors.json';
import defaultSettings from '@/ui/ui_ajax-products/product/default-settings.json';
import methods from '@/ui/ui_ajax-products/methods/index';
import renderStars from '@/ui/ui_ajax-reviews/methods/renderStars';

/**
 * @class
 * @private
 *
 * @description
 * DOM + AjaxProduct
 */
const AjaxProductDOM = function() {
  const self = this;

  self.options = options;
  self.defaultSettings = defaultSettings;
  self.methods = methods;
  self.renderStars = renderStars;

  document.addEventListener('DOMContentLoaded', function() {
    self._init();
    self._loadProductListOnEvent();
  });
};

/**
 * Обновление
 * @method
 */
AjaxProductDOM.prototype.update = function() {
  this._init();
};

/**
 * Загрузка отдельной категории
 * @method
 * @private
 */
AjaxProductDOM.prototype._loadProductListOnEvent = function() {
  const self = this;
  EventBus.subscribe('ui-ajax-product:load-product', function(ajaxProductNode) {
    if (!ajaxProductNode) { return false; }

    const templates = self._getTemplates(ajaxProductNode);

    const noInited = !ajaxProductNode.classList.contains(self.defaultSettings.classes.ajaxProductIsInit);

    if (noInited && templates.product) {
      self._renderProduct(ajaxProductNode, templates);
    }
  });
};

/**
 * Инициализация
 * @method
 * @private
 */
AjaxProductDOM.prototype._init = function() {
  const self = this;

  const ajaxProductNodes = document.querySelectorAll(self.options.ajaxProduct.attr);
  if (!ajaxProductNodes.length) { return; }

  Array.from(ajaxProductNodes).forEach((ajaxProductNode) => {
    const noInited = !ajaxProductNode.classList.contains(self.defaultSettings.classes.ajaxProductIsInit);

    let options = {};
    const templates = self._getTemplates(ajaxProductNode);

    if (noInited && options.initOnLoadPage == 'true' && templates.product) {
      self._renderProduct(ajaxProductNode, templates);
    }
  });
};

/**
 * @private
 *
 * @description
 * Получение данных
 */
AjaxProductDOM.prototype._getProduct = function(productId, options = {}) {
  return new Promise(async resolve => {
    const url = new URL(location.origin + `/front_api/products.json?ids=${productId}.json`);
    const lang = urlTool.getKeyValue('lang') || '';

    let isFetching = false;

    // Отменяем запрос если прилетела отмена
    const controller = new window.AbortController();
    EventBus.subscribe('abort-request:ui-ajax-product', function() {
      if (isFetching) {
        controller.abort();
      }
    });

    if (options.productData && options.productData !== 'false') {
      if (options.productData.every(dataItem => typeof dataItem === 'string')) {
        url.searchParams.append('with', options.productData.toString());
      } else {
        console.error(new Error(`В productsData для товара ${productId} был передан некорректный массив`));
      }
    }

    if (options.imagesLimit && options.imagesLimit !== 'false') {
      url.searchParams.append('images_limit', options.imagesLimit);
    }

    if (options.imageResizingRules && Array.isArray(options.imageResizingRules)) {
      const baseKey = 'image_resizing_rules[]';

      options.imageResizingRules.forEach((rule) => {
        if (typeof rule === 'object') {
          Object.entries(rule).forEach(([ key, value ]) => {
            url.searchParams.append(`${baseKey}[${key}]`, value);
          });
        }
      });
    }

    if (options.videoLinksDetails && options.videoLinksDetails !== 'false') {
      url.searchParams.append('video_links_details', true);
    }

    url.searchParams.append('lang', lang);

    isFetching = true;
    const productsData = await fetch(url, { signal: controller.signal }).then(response => {
      isFetching = false;

      return response.json();
    });

    resolve(productsData);
  });
};

/**
 * @private
 *
 * @description
 * Формируем список товаров
 */
AjaxProductDOM.prototype._renderProduct = async function(productNode, templates) {
  const self = this;

  let options = {};
  const optionsData = productNode.dataset[self.options.ajaxProduct.data];
  if (optionsData) { options = JSON.parse(optionsData); }

  const productId = options.productId;

  if (!productId) { return; }

  (async () => {
    self.methods._toggleLoadingState(productNode, self.options.ajaxProduct.attr, self.defaultSettings.classes.ajaxProductIsLoading);

    const productData = await self._getProduct(productId, {
      productData: options.productData,
      imageResizingRules: options.imageResizingRules,
      imagesLimit: options.imagesLimit,
      videoLinksDetails: options.videoLinksDetails
    });

    try {
      await Products._storage.saveProducts(
        productData.products,
        {
          checkLocale: true,
          withOriginalUpdatedAt: true
        }
      );

      productNode.appendChild(self._createProduct(productData.products[0], options, templates));

      productNode.classList.add(self.defaultSettings.classes.ajaxProductIsInit);
      EventBus.publish('init-product:ui-ajax-product', { productNode: productNode });

      Products._initProduct(productData.products[0]);
    } catch (err) {
      console.error(err);
    } finally {
      self.methods._toggleLoadingState(productNode, self.options.ajaxProduct.attr, self.defaultSettings.classes.ajaxProductIsLoading);
    }
  })();
};

/**
 * @private
 *
 * @description
 * Формируем товар
 */
AjaxProductDOM.prototype._createProduct = function(itemData, options, templates) {
  const self = this;

  const productForm = templates.product.content.querySelector(self.options.ajaxProductForm.attr).cloneNode(true);
  const link = `/product/${itemData.permalink}`;
  const id = itemData.id;
  const availableProduct = itemData.available;
  const productName = itemData.title;
  const productVariants = itemData.variants;
  const forbidOrderOverExisting = Shop.config.config.forbid_order_over_existing;
  const reviewsAverageRating = itemData.reviews_average_rating_cached;
  const reviewsCount = itemData.reviews_count_cached;

  // Будет true только в том случае, если была передана настройка withoutManyVariants и у товара больше 1 варианта
  const isMoreThanOneVariant = itemData.is_more_than_one_variant || false;

  const titleNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductTitle.attr);
  const linkNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductLink.attr);
  const favoritesTriggerNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductFavoritesTrigger.attr);
  const compareTriggerNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductCompareTrigger.attr);
  const stickersNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductStickers.attr);
  const shortDescriptionNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductShortDescription.attr);
  const fullDescriptionNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductFullDescription.attr);
  const variantsSelectBlockNode = self.methods._getFieldNodes(productForm, self.options.ajaxProductVariantsSelectBlock.attr, true);
  const addCartNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductAddCart.attr);
  const productGalleryNode = self.methods._getFieldNodes(productForm, self.options.ajaxProductGallery.attr, true);
  const productOnePhotoNode = self.methods._getFieldNodes(productForm, self.options.ajaxProductOnePhoto.attr, true);
  const productPropertiesNode = self.methods._getFieldNodes(productForm, self.options.ajaxProductProperties.attr, true);
  const productUnitNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductUnit.attr);
  const productSizesTableBtnNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductSizesTableBtn.attr);
  const productReviewsRatingNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductReviewsAverageRating.attr);
  const productReviewsCountNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductReviewsCount.attr);

  if (itemData.list_card_mode) {
    productForm.setAttribute(
      self.options.ajaxProductListCardMode.value, itemData.list_card_mode
    );
  }

  if (itemData.list_card_mode_with_variants) {
    productForm.setAttribute(
      self.options.ajaxProductListCardModeWithVariants.value, itemData.list_card_mode_with_variants
    );
  }

  productReviewsRatingNodes.forEach((item) => {
    if (reviewsAverageRating != null) {
      item.innerHTML += self.renderStars(reviewsAverageRating, {
        baseClass: 'icon-star-o',
        activeClass: 'icon-star'
      }).join('');
    }
  });

  if (itemData.reviews_count_cached) {
    productReviewsCountNodes.forEach((item) => {
      item.textContent = stripHtml(reviewsCount);
    });
  }

  self.setSiteSettings(productForm);

  // bundle
  const bundleDiscountNodes = self.methods._getFieldNodes(productForm, self.options.ajaxProductBundleDiscount.attr);
  const bundleComponentsNode = self.methods._getFieldNodes(productForm, self.options.ajaxProductBundleComponents.attr, true);

  // id
  productForm.setAttribute('data-product-id', id);

  // Название
  titleNodes.forEach((item) => {
    item.textContent = stripHtml(productName);
  });

  // Ссылка перехода в карточку товара
  linkNodes.forEach((item) => {
    item.setAttribute('href', link);
  });

  // Галерея фотографий товара
  if (templates.gallery && (itemData.images && itemData.images.length > 1 || itemData.video_links && itemData.video_links.length > 0)) {
    const galleryData = {
      itemData,
      template: templates.gallery,
      galleryNode: productGalleryNode,
      productName,
      options
    };

    self._createGallery(galleryData);
  } else if (productOnePhotoNode && templates.onePhoto && itemData.images && itemData.images.length) {
    const onePhotoItem = self.methods._getNodeFromTemplate(templates.onePhoto, self.options.ajaxProductOnePhotoItem.attr);

    if (onePhotoItem.hasAttribute(self.options.ajaxProductImageLink.value)) {
      onePhotoItem.setAttribute('href', itemData.first_image.original_url);
    }

    if (onePhotoItem) {
      productOnePhotoNode.appendChild(self._createImage(onePhotoItem, itemData.images[0], itemData.images[0].title || productName));
    }
  }

  // Формирование select-вариантов товара
  self.methods._createVariantsSelector(productVariants, variantsSelectBlockNode, options, self.options.ajaxProductVariantsSelectBlock.data);

  // Стикеры
  if (itemData.properties && itemData.properties.length > 0) {
    const markId = itemData.properties.find(
      (item) => item.permalink == 'label'
    );

    if (markId && itemData.characteristics) {
      itemData.characteristics.forEach((characteristicsItem) => {
        if (characteristicsItem.property_id == markId.id) {
          stickersNodes.forEach((item) => {
            item.innerHTML += self.methods._renderLabel(characteristicsItem.permalink, characteristicsItem.title);
          });
        }
      });
    }
  }

  // Краткое описание
  if (itemData.short_description) {
    const shortDescriptionData = {
      descriptionNodes: shortDescriptionNodes,
      templateNode: templates.shortDescription,
      description: itemData.short_description
    };

    self._createDescription(shortDescriptionData);
  } else if (shortDescriptionNodes.length) {
    shortDescriptionNodes.forEach(item => item.style.display = 'none');
  }

  // Полное описание
  if (itemData.description) {
    const fullDescriptionData = {
      descriptionNodes: fullDescriptionNodes,
      templateNode: templates.fullDescription,
      description: itemData.description
    };

    self._createDescription(fullDescriptionData);
  } else if (fullDescriptionNodes.length) {
    fullDescriptionNodes.forEach(item => item.style.display = 'none');
  }

  // Кнопка добавления в избранное
  favoritesTriggerNodes.forEach((item) => {
    item.setAttribute('data-ui-favorites-trigger', id);
  });

  // Кнопка добавления в сравнение
  compareTriggerNodes.forEach((item) => {
    item.setAttribute('data-compare-trigger', id);
  });

  // Удаление кнопки добавления в корзину
  if (!availableProduct && forbidOrderOverExisting) {
    addCartNodes.forEach((item) => {
      item.remove();
    });
  }

  // Параметры товара
  const propertiesData = {
    productPropertiesNode,
    templateNode: templates.productProperties,
    properties: itemData.properties,
    characteristics: itemData.characteristics
  };

  self._createProperties(propertiesData);

  // Единица измерения товара
  if (productUnitNodes.length) {
    productUnitNodes.forEach(item => {
      item.innerHTML += `${Shop.units.getName(itemData.unit)}`;
    });
  }

  self._checkIfHasSizeTableBtn(productSizesTableBtnNodes, itemData);

  // Вашаем различные классы на элемент формы карточки товара
  self.methods._checkIfHasManyProductVariants(productForm, productVariants, self.defaultSettings.classes.ajaxProductHasManyVariants, isMoreThanOneVariant);
  self.methods._checkIfProductHasManyPrices(productForm, itemData, self.defaultSettings.classes.ajaxProductHasManySalePrices, self.defaultSettings.classes.ajaxProductHasManyOldPrices);

  if (itemData.bundle) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductIsBundle);

    // Если у комплекта есть скидка, и есть ноды для её вывода, то выводим
    if (itemData.bundle_discount && bundleDiscountNodes.length) {
      bundleDiscountNodes.forEach(discountNode => {
        discountNode.textContent = Shop.money.format(itemData.bundle_discount);
      });
    }

    // Рендер состава комплекта
    self._createBundleComponents(itemData, bundleComponentsNode, templates);
  }

  return productForm;
};

/**
 * @private
 *
 * @description
 * Рендер галереи
 */
AjaxProductDOM.prototype._createGallery = function(galleryData) {
  const self = this;

  const itemData = galleryData.itemData;
  const galleryTemplate = galleryData.template;
  const galleryNode = galleryData.galleryNode;
  const options = galleryData.options;

  if (!galleryNode || !galleryTemplate) {
    return;
  }

  if (options.videoBeforeImage && options.videoBeforeImage !== 'false') {
    if (itemData.video_links_details && itemData.video_links_details.length) {
      galleryNode.classList.add(self.defaultSettings.classes.ajaxProductGalleryVideoBeforeImage);
    }

    self._createGalleryVideoItem(galleryData);
    if (itemData.images) {
      self._createGalleryImageItem(galleryData);
    }
  } else {
    if (itemData.images) {
      self._createGalleryImageItem(galleryData);
    }
    self._createGalleryVideoItem(galleryData);
  }
};

/**
 * @private
 *
 * @description
 * Рендер галереи
 */
AjaxProductDOM.prototype._createGalleryVideoItem = function(galleryData) {
  const self = this;

  const itemData = galleryData.itemData;
  const galleryTemplate = galleryData.template;
  const galleryNode = galleryData.galleryNode;

  if (itemData.video_links_details && itemData.video_links_details.length) {
    itemData.video_links_details.forEach((media) => {
      if (media.video_preview_url || media.iframe) {
        const galleryMainIframe = self.methods._getNodeFromTemplate(galleryTemplate, self.options.ajaxProductGalleryMainIframe.attr);
        const galleryThumbIframe = self.methods._getNodeFromTemplate(galleryTemplate, self.options.ajaxProductGalleryThumbIframe.attr);

        galleryNode.appendChild(self._createVideo('iframe', galleryMainIframe, media));
        galleryNode.appendChild(self._createVideo('iframe', galleryThumbIframe, media));
      } else {
        const galleryMainVideo = self.methods._getNodeFromTemplate(galleryTemplate, self.options.ajaxProductGalleryMainVideo.attr);
        const galleryThumbVideo = self.methods._getNodeFromTemplate(galleryTemplate, self.options.ajaxProductGalleryThumbVideo.attr);

        galleryNode.appendChild(self._createVideo('video', galleryMainVideo, media));
        galleryNode.appendChild(self._createVideo('video', galleryThumbVideo, media));
      }
    });
  }
};

/**
 * @private
 *
 * @description
 * Рендер галереи
 */
AjaxProductDOM.prototype._createGalleryImageItem = function(galleryData) {
  const self = this;

  const itemData = galleryData.itemData;
  const galleryTemplate = galleryData.template;
  const galleryNode = galleryData.galleryNode;
  const productName = galleryData.productName;

  itemData.images.forEach((image) => {
    const galleryImagesItem = self.methods._getNodeFromTemplate(galleryTemplate, self.options.ajaxProductGalleryImageItem.attr);
    const galleryThumbsItem = self.methods._getNodeFromTemplate(galleryTemplate, self.options.ajaxProductGalleryThumbImageItem.attr);

    if (galleryImagesItem) {
      galleryNode.appendChild(self._createImage(galleryImagesItem, image, image.title || productName));
    }

    if (galleryThumbsItem) {
      galleryNode.appendChild(self._createImage(galleryThumbsItem, image, image.title || productName));
    }
  });
};

/**
 * @private
 *
 * @description
 * Рендер картинок
 */
AjaxProductDOM.prototype._createImage = function(productPicture, image, title = '') {
  const self = this;

  productPicture.setAttribute(self.options.ajaxProductImageId.value, image.id);

  const pictureSources = self.methods._getFieldNodes(productPicture, 'source');
  const pictureImg = productPicture.querySelector('img');
  const pictureLinks = self.methods._getFieldNodes(productPicture, self.options.ajaxProductImageLink.attr);

  pictureSources.forEach((item) => {
    const url = self.methods._imageUrlForPictureSource(
      image,
      item,
      self.options.ajaxProductImageResizingRulesIndex.data
    );

    // Плавно переходим на нативный lazyload, условие нужно чтобы ничего не сломать в кастомных виджетах
    if (item.classList.contains('lazyload') && typeof LazyLoad === 'function') {
      item.setAttribute('data-srcset', url);
    } else {
      item.setAttribute('srcset', url);
    }
  });

  if (pictureImg) {
    const url = self.methods._imageUrlForPictureSource(
      image,
      pictureImg,
      self.options.ajaxProductImageResizingRulesIndex.data
    );

    // Плавно переходим на нативный lazyload, условие нужно чтобы ничего не сломать в кастомных виджетах
    if (pictureImg.classList.contains('lazyload') && typeof LazyLoad === 'function') {
      pictureImg.setAttribute('data-src', url);
    } else {
      pictureImg.setAttribute('src', url);
    }

    pictureImg.setAttribute('alt', title);
    pictureImg.setAttribute('title', title);
  }

  if (pictureLinks.length) {
    pictureLinks.forEach(link => {
      link.setAttribute('href', image.original_url);
    });
  }

  return productPicture;
};

/**
 * @private
 *
 * @description
 * Рендерим видео
 */
AjaxProductDOM.prototype._createVideo = function(type, node, media) {
  const self = this;

  if (!node) { return; }

  const mediaLinkNode = self.methods._getFieldNodes(node, self.options.ajaxProductGalleryMediaLink.attr, true);
  const mediaIndexNode = self.methods._getFieldNodes(node, self.options.ajaxProductGalleryMediaIndexHref.attr, true);
  const mediaPreviewNode = self.methods._getFieldNodes(node, self.options.ajaxProductGalleryMediaPreview.attr, true);
  const mediaContentNode = self.methods._getFieldNodes(node, self.options.ajaxProductGalleryMediaContent.attr, true);

  if (type === 'iframe') {
    if (mediaPreviewNode && media.video_preview_url) {
      if (media.video_preview_url.includes('http://')) {
        media.video_preview_url = media.video_preview_url.replace('http://', 'https://');
      }

      mediaPreviewNode.setAttribute('src', media.video_preview_url);
    }

    if (mediaIndexNode) {
      mediaIndexNode.setAttribute('href', `#video-${media.id}`);
    }

    if (mediaContentNode && media.iframe) {
      mediaContentNode.innerHTML = media.iframe;
      mediaContentNode.querySelector('iframe').setAttribute('id', `video-${media.id}`);
    }
  } else {
    if (mediaPreviewNode) {
      mediaPreviewNode.setAttribute('src', 'https://static.insales-cdn.com/files/1/4301/24817869/original/play-button.png');
    }

    if (mediaContentNode) {
      mediaContentNode.outerHTML = `
        <video
          autoplay="true"
          oncanplay="this.muted=true"
          muted="true"
          loop="true"
          playsinline="true"
          width="100%"
          class="lazy"
          preload="metadata"
          id="#video-${media.id}">
          <source src="${media.url}" type="video/mp4">
        </video>
      `;
    }
  }

  if (mediaLinkNode) {
    mediaLinkNode.setAttribute('href', media.url);
  }

  return node;
};

/**
 * @private
 *
 * @description
 * Метод для рендера описания
 */
AjaxProductDOM.prototype._createDescription = function(params) {
  const self = this;

  const descriptionNodes = params.descriptionNodes;
  const templateNode = params.templateNode;
  const description = params.description;

  if (!descriptionNodes.length || !templateNode) { return; }
  if (description && description !== '') {
    descriptionNodes.forEach((item) => {
      const shortDescriptionItem = self.methods._getNodeFromTemplate(templateNode, null, null, true);

      const shortDescriptionTextNodes = self.methods._getFieldNodes(shortDescriptionItem, self.options.ajaxProductShortDescriptionItemText.attr);

      if (shortDescriptionTextNodes.length) {
        shortDescriptionTextNodes.forEach(textNode => textNode.innerHTML = description);
      }

      item.appendChild(shortDescriptionItem);
    });
  }
};

/**
 * @private
 * @description
 * Метод для рендера параметров
 *
 * @param {Object} params - Параметры
 * @param {HTMLElement} params.productPropertiesNode - Нода с параметрами
 * @param {HTMLElement} params.templateNode - Шаблон для параметров
 * @param {Array} params.properties - Массив с параметрами
 * @param {Array} params.characteristics - Массив с характеристиками
 *
 * @returns {void}
 */
AjaxProductDOM.prototype._createProperties = function(params) {
  const self = this;

  const { productPropertiesNode: propertiesNode, templateNode, properties, characteristics } = params;

  if (!propertiesNode || !templateNode || !properties || properties && !properties.length) {
    if (propertiesNode) {
      propertiesNode.style.display = 'none';
    }

    return;
  }

  // Получаем настройки
  let propertiesOptions = propertiesNode.dataset[self.options.ajaxProductProperties.data];
  if (propertiesOptions) { propertiesOptions = JSON.parse(propertiesOptions); }
  const { excludeHandles, maxCount = 100, maxVisibleItemsCount } = propertiesOptions;

  // Если в массиве только исключённые параметры, то не делаем перебор и скрываем блок с параметрами
  let filteredProperties = properties.filter(property => {
    if (property.is_hidden) { return false; }

    if (excludeHandles) {
      excludeHandles.split(',').forEach(item => {
        if (item === property.permalink) {
          property.excluded = true;
        }
      });
    }

    return !property.excluded;
  });

  if (!filteredProperties.length) {
    propertiesNode.style.display = 'none';

    return;
  }

  if (maxCount && filteredProperties.length > maxCount) {
    filteredProperties = filteredProperties.slice(0, maxCount);
  }

  let index = 1;
  filteredProperties.forEach((property) => {
    // Клонируем шаблон для каждого параметра
    const propertyItem = self.methods._getNodeFromTemplate(templateNode, self.options.ajaxProductProperty.attr);

    // Достаём ноды тайтлов
    const propertyTitleNodes = self.methods._getFieldNodes(propertyItem, self.options.ajaxProductPropertyTitle.attr);
    const characteristicTitleNodes = self.methods._getFieldNodes(propertyItem, self.options.ajaxProductPropertyCharacteristicTitle.attr);

    if (propertyTitleNodes.length) {
      propertyTitleNodes.forEach(node => node.textContent = property.title);
    }

    if (characteristicTitleNodes.length) {
      characteristicTitleNodes.forEach(node => {
        const propertyCharacteristic = characteristics.filter(char => char.property_id === property.id);

        node.textContent = propertyCharacteristic.map(item => item.title).join(', ');
      });
    }

    // Если было передано больше параметров чем должно отображаться сразу, то для лишних задаём классы
    if (maxVisibleItemsCount && index > maxVisibleItemsCount) {
      propertyItem.classList.add(self.defaultSettings.classes.ajaxProductPropertyHidden);
    }

    propertiesNode.appendChild(propertyItem);
    index++;
  });

  // Если в шаблоне есть кнопки "Показать все" и "Скрыть", то достаём их и вставляем в разметку
  const showMoreBtn = self.methods._getNodeFromTemplate(templateNode, self.options.ajaxProductPropertiesShowMoreBtn.attr, true);

  const hideBtn = self.methods._getNodeFromTemplate(templateNode, self.options.ajaxProductPropertiesHideBtn.attr, true);

  if (showMoreBtn && maxVisibleItemsCount && index > maxVisibleItemsCount) {
    propertiesNode.appendChild(showMoreBtn);
  }

  if (hideBtn && maxVisibleItemsCount && index > maxVisibleItemsCount) {
    propertiesNode.appendChild(hideBtn);
  }
};

/**
 * @private
 *
 * @description
 * Рендер состава комплекта
 */
AjaxProductDOM.prototype._createBundleComponents = function(itemData, bundleComponentsNode, templates) {
  const self = this;

  if (!itemData.product_bundle_components || !itemData.product_bundle_components.length) { return; }

  // Получаем массив с ID товаров которые входят в комплект
  const bundleItemsIds = itemData.product_bundle_components.map(item => item.product_id).join(',');

  (async () => {
    // Вешаем лоадер и шлём запрос на получение айтемов
    self.methods._toggleLoadingState(bundleComponentsNode, self.options.ajaxProductBundleComponents.attr, self.defaultSettings.classes.ajaxProductBundleItemsLoading);

    const bundleItems = await self._getBundleComponents(bundleItemsIds);

    try {
      if (bundleItems.products.length) {
        bundleItems.products.forEach(bundleItem => {
          // Получаем данные конкретного айтема из product_bundle_components объекта товара (комплекта)
          let productComponentData = itemData.product_bundle_components.filter(item => item.product_id === bundleItem.id)[0];

          bundleComponentsNode.appendChild(self._renderBundleItem(productComponentData, bundleItem, templates));

          bundleComponentsNode.classList.add(self.defaultSettings.classes.ajaxProductBundleItemsLoaded);
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      self.methods._toggleLoadingState(bundleComponentsNode, self.options.ajaxProductBundleComponents.attr, self.defaultSettings.classes.ajaxProductBundleItemsLoading);
      EventBus.publish('bundle-items-loaded:ui-ajax-product', { bundleComponentsNode });
    }
  })();
};

/**
 * @private
 *
 * @description
 * Получение данных для состава комплектов
 */
AjaxProductDOM.prototype._getBundleComponents = function(itemsIds) {
  return new Promise(async resolve => {
    const url = new URL(location.origin + `/front_api/products.json?ids=${itemsIds}.json`);
    const lang = urlTool.getKeyValue('lang') || '';

    url.searchParams.append('with', 'variants,first_image,images,option_names, price_kinds');

    url.searchParams.append('lang', lang);

    const productsData = await fetch(url).then(response => {
      return response.json();
    });

    resolve(productsData);
  });
};

/**
 * @private
 *
 * @description
 * Рендер компонентов комплекта
 */
AjaxProductDOM.prototype._renderBundleItem = function(componentsData, bundleItemData, templates) {
  const self = this;

  // Клонируем ноду из темплейта
  const bundleItemNode = self.methods._getNodeFromTemplate(templates.bundleItem, self.options.ajaxProductBundleComponentsItem.attr);

  // Получаем все ноды для айтема комплекта
  const bundleItemTitleNodes = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemTitle.attr);
  const bundleItemLinkNodes = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemLink.attr);
  const bundleItemImageNode = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemImage.attr, true);
  const bundleItemOptionsNode = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemOptions.attr, true);
  const bundleItemProductPriceNode = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemProductPrice.attr, true);
  const bundleItemVariantPriceNode = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemVariantPrice.attr, true);
  const bundleItemQuantityNodes = self.methods._getFieldNodes(bundleItemNode, self.options.ajaxProductBundleComponentsItemQuantity.attr);

  // Получаем данные варианта айтема комплекта
  let itemVariantData = null;
  let itemVariantImage = null;

  if (bundleItemData.variants && bundleItemData.variants.length) {
    itemVariantData = bundleItemData.variants.filter(variant => variant.id === componentsData.variant_id)[0];
  }

  if (bundleItemData.images && bundleItemData.images.length) {
    itemVariantImage = bundleItemData.images.filter(image => image.id === itemVariantData.image_id)[0];
  }

  // Если у товара нулевая цена, или у выбранного варианта, то вешаем класс
  if (componentsData.free || !itemVariantData || !itemVariantData.price || !parseInt(itemVariantData.price)) {
    bundleItemNode.classList.add(self.defaultSettings.classes.ajaxProductBundleItemIsFree);
  }

  // Название айтема
  if (bundleItemTitleNodes.length) {
    bundleItemTitleNodes.forEach(titleNode => {
      titleNode.textContent = bundleItemData.title;
    });
  }

  // Ссылки на айтем
  if (bundleItemLinkNodes.length) {
    bundleItemLinkNodes.forEach(linkNode => {
      linkNode.setAttribute('href', `${bundleItemData.url}?variant_id=${componentsData.variant_id}`);
    });
  }

  // Картинка
  if (bundleItemImageNode) {
    if (itemVariantImage) {
      bundleItemImageNode.setAttribute('src', itemVariantImage.medium_url);
    } else if (bundleItemData.first_image) {
      bundleItemImageNode.setAttribute('src', bundleItemData.first_image.medium_url);
    }

    bundleItemImageNode.setAttribute('alt', bundleItemData.title);
    bundleItemImageNode.setAttribute('title', bundleItemData.title);
  }

  // Цена айтема
  if (bundleItemProductPriceNode) {
    bundleItemProductPriceNode.innerHTML = Shop.money.format(bundleItemData.price_min);
  }

  // Цена варианта айтема
  if (bundleItemVariantPriceNode && !componentsData.free && itemVariantData) {
    bundleItemVariantPriceNode.innerHTML = Shop.money.format(itemVariantData.price);
  }

  // Количество этого айтема в комплекте
  if (bundleItemQuantityNodes.length) {
    bundleItemQuantityNodes.forEach(quantityItem => {
      quantityItem.textContent = componentsData.quantity;
    });
  }

  // Названия и значения опций
  if (bundleItemOptionsNode && bundleItemData.option_names && bundleItemData.option_names.length && itemVariantData) {
    bundleItemData.option_names.forEach(name => {
      bundleItemOptionsNode.appendChild(self._renderBundleItemOptions(name, itemVariantData.option_values, templates));
    });
  }

  return bundleItemNode;
};

/**
 * @private
 *
 * @description
 * Рендер названий свойств и значений компонента комплекта
 */
AjaxProductDOM.prototype._renderBundleItemOptions = function(name, values, templates) {
  const self = this;

  // Получаем value для текущего option_name
  const currentValue = values.filter(value => value.option_name_id === name.id)[0];

  // Клонируем ноду из шаблона для вывода опций
  const optionsItemNode = self.methods._getNodeFromTemplate(templates.bundleOptionsItem, self.options.ajaxProductBundleComponentsItemOptionsItem.attr);

  // Получаем ноды для вывода option_name и option_value и затем выводим их
  const optionsItemNameNode = self.methods._getFieldNodes(optionsItemNode, self.options.ajaxProductBundleComponentsItemOptionsItemNameTitle.attr, true);
  const optionsItemValueNode = self.methods._getFieldNodes(optionsItemNode, self.options.ajaxProductBundleComponentsItemOptionsItemValueTitle.attr, true);

  optionsItemNameNode.textContent = name.title;
  optionsItemValueNode.textContent = currentValue.title;

  return optionsItemNode;
};

/**
 * Проверяем есть ли у товара кнопка для вызова таблицы размеров с названиями свойства "Размер" в магазине, и если есть, то проверяем есть ли у товара свойство содержащее хотя бы одно из переданных значений. Если нет, то скрываем кнопку
 * @method
 * @private
 */
AjaxProductDOM.prototype._checkIfHasSizeTableBtn = function(productSizesTableBtnNodes, itemData) {
  const self = this;

  if (productSizesTableBtnNodes.length) {
    productSizesTableBtnNodes.forEach(tableBtn => {
      const sizeOptionNames = tableBtn.dataset[self.options.ajaxProductSizesTableBtn.data];

      if (itemData.bundle || sizeOptionNames && !itemData.option_names.filter(item => sizeOptionNames.split(',').includes(item.title))) {
        tableBtn.style.display = 'none';
      }
    });
  }
};

/**
 * Получаем ноды темплейтов из DOM
 * @method
 * @private
 */
AjaxProductDOM.prototype._getTemplates = function(ajaxProductNode) {
  const self = this;
  const templates = {};

  templates.product = ajaxProductNode.querySelector(self.options.ajaxProductTemplate.attr);

  templates.gallery = ajaxProductNode.querySelector(self.options.ajaxProductGalleryTemplate.attr);

  templates.onePhoto = ajaxProductNode.querySelector(self.options.ajaxProductOnePhotoTemplate.attr);

  templates.shortDescription = ajaxProductNode.querySelector(self.options.ajaxProductShortDescriptionTemplate.attr);

  templates.fullDescription = ajaxProductNode.querySelector(self.options.ajaxProductFullDescriptionTemplate.attr);

  templates.productProperties = ajaxProductNode.querySelector(self.options.ajaxProductPropertiesTemplate.attr);

  templates.bundleItem = ajaxProductNode.querySelector(self.options.ajaxProductBundleComponentsItemTemplate.attr);

  templates.bundleOptionsItem = ajaxProductNode.querySelector(self.options.ajaxProductBundleComponentsItemOptionsTemplate.attr);

  return templates;
};

/**
 * Установка настроек сайта
 * @method
 * @private
 * @param {HTMLElement} productForm - Нода формы товара
 *
 * @return {void}
 *
 * @todo В будущем мы планируем сделать, чтобы эти настройки прилетали для каждого товара индивиудально. Пока это не реализовано мы берём настройки из Shop.config.config.site_setting.
 */
AjaxProductDOM.prototype.setSiteSettings = function(productForm) {
  const self = this;

  const siteSettings = window.Shop.config.config.site_setting;

  if (!siteSettings) { return; }

  if (siteSettings.show_quick_checkout_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductShowQuickCheckoutButton);
  }

  if (siteSettings.show_marketplace_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductShowMarketplaceButton);
  }

  if (siteSettings.show_service_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductShowServiceButton);
  }

  if (siteSettings.show_cart_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductShowCartButton);
  }

  if (!siteSettings.show_quick_checkout_button && !siteSettings.show_marketplace_button && !siteSettings.show_service_button && !siteSettings.show_cart_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductHideAllButtons);
  }
};

export default _Singleton(AjaxProductDOM).getInstance();
